import React, { useContext, useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { patchEndpoint } from "../../../services/integration.service";
import { ToastrContext } from "../../../provider/toastrContext";
import {
  EVENTS_GROUPED_WIRED,
  EVENTS_ADVANCED,
} from "../eventCatalogue";

const EventCheckbox = ({ option, checked, onChange }) => (
  <div className="col-md-6 mb-1" key={option.value}>
    <Form.Check
      type="checkbox"
      id={`evt-edit-${option.value}`}
      checked={!!checked}
      onChange={(e) => onChange(option.value, e.target.checked)}
      label={
        <span title={option.description}>
          <code className="me-1">{option.label}</code>
          {option.notWired && (
            <span className="text-muted small">(not yet wired)</span>
          )}
        </span>
      }
    />
  </div>
);

const EventPicker = ({ value, onChange, error }) => {
  const selected = new Set(value || []);
  const toggle = (v, on) => {
    const next = new Set(selected);
    if (on) next.add(v);
    else next.delete(v);
    onChange(Array.from(next));
  };
  return (
    <div>
      {EVENTS_GROUPED_WIRED.map((group) => (
        <div key={group.label} className="mb-3">
          <div className="text-muted small text-uppercase mb-1">
            {group.label}
          </div>
          <div className="row">
            {group.options.map((o) => (
              <EventCheckbox
                key={o.value}
                option={o}
                checked={selected.has(o.value)}
                onChange={toggle}
              />
            ))}
          </div>
        </div>
      ))}
      <details className="mb-2">
        <summary className="text-muted small" style={{ cursor: "pointer" }}>
          Advanced — events not yet wired (no deliveries fire yet)
        </summary>
        <div className="row mt-2">
          {EVENTS_ADVANCED.map((o) => (
            <EventCheckbox
              key={o.value}
              option={o}
              checked={selected.has(o.value)}
              onChange={toggle}
            />
          ))}
        </div>
      </details>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

const arraysEqual = (a, b) => {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
};

const EditEndpointModal = ({ show, onClose, endpoint, onSaved }) => {
  const { showSuccess, showError } = useContext(ToastrContext);
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      url: "",
      events: [],
      status: "enabled",
      description: "",
    },
  });

  useEffect(() => {
    if (!show || !endpoint) return;
    reset({
      url: endpoint.url || "",
      events: Array.isArray(endpoint.subscribed_events)
        ? endpoint.subscribed_events
        : [],
      // Treat auto_disabled as disabled in the form; saving "enabled" re-enables it.
      status: endpoint.status === "enabled" ? "enabled" : "disabled",
      description: endpoint.description || "",
    });
  }, [show, endpoint, reset]);

  const onSubmit = async (data) => {
    if (!endpoint) return;
    if (!data.events || data.events.length === 0) {
      showError("Pick at least one event");
      return;
    }
    const body = {};
    if (data.url !== endpoint.url) body.url = data.url;
    if (!arraysEqual(data.events, endpoint.subscribed_events || [])) {
      body.subscribed_events = data.events;
    }
    const normalizedStatus =
      endpoint.status === "enabled" ? "enabled" : "disabled";
    if (data.status !== normalizedStatus) body.status = data.status;
    if ((data.description || "") !== (endpoint.description || "")) {
      body.description = data.description || "";
    }
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      const res = await patchEndpoint(endpoint.id, body);
      showSuccess("Endpoint updated");
      onSaved(res);
      onClose();
    } catch (e) {
      showError((e && e.message) || "Failed to update endpoint");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Webhook Endpoint</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          {endpoint && endpoint.status === "auto_disabled" && (
            <div className="alert alert-warning">
              This endpoint was <strong>auto-disabled</strong> after repeated
              delivery failures. Setting status to <code>enabled</code> below
              will re-enable it.
            </div>
          )}

          <div className="mb-3">
            <label className="col-form-label">
              URL<span className="required">*</span>
            </label>
            <input
              type="url"
              className={`form-control ${errors.url ? "is-invalid" : ""}`}
              {...register("url", {
                required: "URL is required",
                pattern: {
                  value: /^https?:\/\//i,
                  message: "Must start with http:// or https://",
                },
              })}
            />
            {errors.url && (
              <div className="invalid-feedback">{errors.url.message}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="col-form-label">Status</label>
            <Form.Select {...register("status", { required: true })}>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </Form.Select>
          </div>

          <div className="mb-3">
            <label className="col-form-label">Description</label>
            <textarea
              className="form-control"
              rows={2}
              {...register("description")}
            />
          </div>

          <div className="mb-1">
            <label className="col-form-label">
              Subscribed Events<span className="required">*</span>
            </label>
          </div>
          <Controller
            name="events"
            control={control}
            rules={{
              validate: (v) =>
                (v && v.length > 0) || "Pick at least one event",
            }}
            render={({ field }) => (
              <EventPicker
                value={field.value}
                onChange={field.onChange}
                error={errors.events && errors.events.message}
              />
            )}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditEndpointModal;
